import type { DataHue } from '../primitives/CategoryTag/index.js';
import type { MetricClass } from '../primitives/MetricCard/metric-class.js';

/**
 * Card-relevant metric-class lookups (`METRIC_CLASSES`, `MetricClass`,
 * `METRIC_ACCENT_CLASS`, `METRIC_TILE_CLASS`) live colocated with
 * `MetricCard` — a base primitive shouldn't import from this reports
 * layer. Re-exported here so existing `../chart-palette` importers are
 * unaffected. `METRIC_CHART_COLOR` below stays chart-specific (report
 * pages look it up by the SAME `MetricClass` key a `MetricCard`'s
 * `metricClass` prop uses, which is the single-source guarantee that
 * keeps a card and its companion chart in the same color for one metric).
 */
export {
  METRIC_CLASSES,
  type MetricClass,
  METRIC_ACCENT_CLASS,
  METRIC_TILE_CLASS,
} from '../primitives/MetricCard/metric-class.js';

/** Recharts stroke/fill class per metric class. */
export const METRIC_CHART_COLOR: Record<MetricClass, string> = {
  revenue: 'var(--color-success)',
  count: 'var(--color-info)',
  utilization: 'var(--color-data-violet)',
  time: 'var(--color-data-cyan)',
  online: 'var(--color-success)',
  walkin: 'var(--color-success)',
  quality: 'var(--color-warning)',
  error: 'var(--color-danger)',
  throughput: 'var(--color-data-emerald)',
  customers: 'var(--color-data-pink)',
  tips: 'var(--color-data-amber)',
  ai: 'var(--color-data-violet)',
  neutral: 'var(--color-muted)',
};

/**
 * Bar / stacked-segment / legend-swatch fill class per data hue. The palette
 * owns the raw `bg-data-*` token (data-tile gate allowlist) so chart code can
 * pass a `hue`. Use ONLY for data-viz fills (proportional bars, distribution
 * segments, legend dots) — KPI tiles use `MetricCard` / `SeverityRampItem`.
 */
export const DATA_HUE_CHART_CLASS: Record<DataHue, string> = {
  green: 'bg-data-green',
  emerald: 'bg-data-emerald',
  blue: 'bg-data-blue',
  cyan: 'bg-data-cyan',
  violet: 'bg-data-violet',
  purple: 'bg-data-purple',
  amber: 'bg-data-amber',
  yellow: 'bg-data-yellow',
  orange: 'bg-data-orange',
  red: 'bg-data-red',
  pink: 'bg-data-pink',
  gray: 'bg-data-gray',
};

/** Fill class for a chart bar/segment/legend swatch of the given data hue. */
export function dataHueChartClass(hue: DataHue): string {
  return DATA_HUE_CHART_CLASS[hue];
}

/**
 * Data-hue fill VALUE (a `var(--color-data-*)` string) — for chart libraries
 * (Recharts) that take a `fill`/`stroke` color value rather than a class.
 * Mirrors `DATA_HUE_CHART_CLASS` (which is the Tailwind-class form for legend
 * swatches / HTML fills). Same data-viz-only scope.
 */
export const DATA_HUE_CHART_FILL: Record<DataHue, string> = {
  green: 'var(--color-data-green)',
  emerald: 'var(--color-data-emerald)',
  blue: 'var(--color-data-blue)',
  cyan: 'var(--color-data-cyan)',
  violet: 'var(--color-data-violet)',
  purple: 'var(--color-data-purple)',
  amber: 'var(--color-data-amber)',
  yellow: 'var(--color-data-yellow)',
  orange: 'var(--color-data-orange)',
  red: 'var(--color-data-red)',
  pink: 'var(--color-data-pink)',
  gray: 'var(--color-data-gray)',
};

/**
 * Categorical hue rotation for distribution charts (donut/pie segments, stacked
 * bars) where each slice is a distinct category. Ordered for maximum adjacent
 * distinctness and to LEAD with the neutral-categorical hues — the semantically
 * loaded ones (green≈success/brand, red≈error, gray≈neutral) sit at the tail so
 * a chart with a handful of segments never accidentally speaks "good/bad/none".
 * Assign by index: `chartHueAt(i)`.
 */
export const CHART_HUE_ROTATION: readonly DataHue[] = [
  'violet',
  'blue',
  'cyan',
  'amber',
  'pink',
  'orange',
  'emerald',
  'purple',
  'yellow',
  'green',
  'red',
  'gray',
];

/** The categorical hue for the Nth segment (wraps if there are more segments than hues). */
export function chartHueAt(index: number): DataHue {
  return CHART_HUE_ROTATION[index % CHART_HUE_ROTATION.length]!;
}
